import download from "downloadjs";
import moment from "moment";
import {Button} from "primereact/button";
import {Column} from "primereact/column";
import {ColumnGroup} from "primereact/columngroup";
import {DataTable} from "primereact/datatable";
import {Dropdown} from "primereact/dropdown";
import {InputText} from "primereact/inputtext";
import {Row} from "primereact/row";
import React, {useContext, useEffect, useState} from 'react';
import AppContext from "../../AppContext";
import {ActiveFilter} from "../../components/filters/ActiveFilter";
import ExtraMenu from "../../components/Grid/ExtraMenu";
import OptionalDate from "../../components/OptionalDate/OptionalDate";
import {hideLoading, showLoading} from "../../components/Overlay/Loading";
import {GridTemplates} from "../../GridTemplates";
import ArrayUtils from "../../utils/ArrayUtils";
import NetUtils from "../../utils/NetUtils";
import {NumberUtils} from "../../utils/NumberUtils";
import RouterUtils from "../../utils/RouterUtils";
import SearchUtils from "../../utils/SearchUtils";
import {dynamicSort} from "../../utils/SortUtils";
import AcquisitiontypeService from "../Acquisitiontypes/AcquisitiontypeService";
import FinancingService from "../Financing/FinancingService";
import DivestmentService from "./DivestmentService";

const BOUGHT_ID = -999;
const CASH_ID = 10000;
const LOAN_ID = 10001;
const INSTALMENT_ID = 10002;
const INVENTORY_ADJUSTMENT_ID = 10006;
const BOUGHT_ID_ARRAY = [CASH_ID, LOAN_ID, INSTALMENT_ID, INVENTORY_ADJUSTMENT_ID];

const DivestmentGrid = props => {
    const NAME = 'DivestmentGrid'
    const context = useContext(AppContext);
    const [filter, setFilter] = useState({
        active: context.getFilter(NAME, 'active', 1),
        type: context.getFilter(NAME, 'type', undefined),
        financingtype: context.getFilter(NAME, 'financingtype', undefined),
        startDate: context.getFilter(NAME, 'startDate', ''),
        endDate: context.getFilter(NAME, 'endDate', ''),
        search: context.getFilter(NAME, 'search', '')
    });
    const [list, setList] = useState([]);
    const [typeList, setTypeList] = useState([]);
    const [financingtypeList, setFinancingtypeList] = useState([]);

    const matchActive = a => filter.active === null || a.active === filter.active;
    const matchType = a => !filter.type || a.acquisitiontype === filter.type;


    const matchFinancingtype = a => filter.financingtype === BOUGHT_ID
        ? BOUGHT_ID_ARRAY.includes(a.financing)
        : !filter.financingtype || a.financing === filter.financingtype;

    const matchStartDate = a => filter.startDate.length === 0 || a.divestmentDate >= moment(filter.startDate).valueOf();
    const matchEndDate = a => filter.endDate.length === 0 || a.divestmentDate <= moment(filter.endDate).valueOf();


    const matchSearch = a => SearchUtils.isMatching(a, filter.search);

    const filteredList = list.filter(a => matchActive(a) &&
        matchType(a) && matchFinancingtype(a) &&
        matchStartDate(a) && matchEndDate(a) &&
        matchSearch(a)
        // && a.name === 'Utrangerat december'
    )

    const updateFilter = (name, value) => {
        context.setFilter(NAME, name, value)
        filter[name] = value;
        setFilter(filter);
    }

    const onRowSelect = model => RouterUtils.navigateTo(props, '/divestments/' + model.id);

    const exportAccountingExcel = () => {
        const queryString = filteredList.map(a => `idList=${a.id}`).join('&');
        fetch(`/api/divestments/excel-with-rows?${queryString}`, {credentials: 'include'})
            .then(res => res.blob())
            .then(blob => download(blob, 'Bokföringsunderlag.xls'));
    };

    const sumByField = field => filteredList.reduce((sum, a) => sum += !isNaN(a[field]) ? a[field] : 0, 0);
    const totalPrice = sumByField('price');
    let footerGroup = <ColumnGroup>
        <Row>
            <Column footer={'Totalt (' + filteredList.length + ' st)'}
                    style={{textAlign: 'left'}}/>
            <Column footer=''/>
            <Column footer=''/>
            <Column footer=''/>
            <Column footer=''/>
            <Column footer=''/>
            <Column footer=''/>
            <Column footer=''/>
            <Column footer={NumberUtils.formatCurrency(ArrayUtils.sum(filteredList, 'acquisitionPrice'))}
                    style={{textAlign: 'right'}}/>
            <Column footer={NumberUtils.formatCurrency(ArrayUtils.sum(filteredList, 'depreciationValue'))}
                    style={{textAlign: 'right'}}/>
            <Column footer={NumberUtils.formatCurrency(totalPrice)} style={{textAlign: 'right'}}/>
        </Row>
    </ColumnGroup>;


    useEffect(() => {
        showLoading()
        Promise.all([
            DivestmentService.findByCompany(context.currentCompany.id),
            AcquisitiontypeService.findByCompany(context.currentCompany.id),
            FinancingService.getTypes()
        ]).then(([_list, _typeList, _financingtypeList]) => {
            _list.forEach(a => {
                a.priceSek = a.price * a.startExchangeRate;
                a.divestmentDateString = moment(a.divestmentDate).format('YYYY-MM-DD');
            })
            _list.sort(dynamicSort('name'))
            setList(_list)

            _typeList.sort(dynamicSort('name'));
            setTypeList(_typeList.filter(t => t.active === 1));


            _financingtypeList.sort(dynamicSort('nameTranslated'));
            setFinancingtypeList([{id: BOUGHT_ID, nameTranslated: 'Köpt'}, ..._financingtypeList]);

            hideLoading()
        });
    }, [context.currentCompany.id])


    const columns = [
        {field: 'name', header: 'Namn', className: 'nowrap'},
        {field: 'acquisitiontypeString', header: 'Typ', className: 'nowrap', style: {width: '150px'}},
        {field: 'customerString', header: 'Kund', className: 'nowrap', style: {width: '200px'}},
        {field: 'financingString', header: 'Finansiering', className: 'nowrap'},
        {field: 'reallyDivested', header: 'Avyttrad', style: {width: '90px'}, body: GridTemplates.boolean},
        {field: 'divestmentDateString', header: 'Datum', style: {width: '90px'}},
        {field: 'accountNumber', header: 'Kontonr', style: {width: '90px'}, className: 'nowrap'},
        {field: 'bookkeepingId', header: 'Bokförings-ID', style: {width: '130px'}, className: 'nowrap'},
        {
            field: 'acquisitionPrice',
            header: 'Anskaffningspris',
            style: {width: '120px', textAlign: 'right'},
            body: GridTemplates.currency
        },
        {
            field: 'depreciationValue',
            header: 'Ack.avskrivningar',
            style: {width: '120px', textAlign: 'right'},
            body: GridTemplates.currency
        },
        {
            field: 'price',
            header: 'Pris',
            style: {width: '120px', textAlign: 'right'},
            body: GridTemplates.integer
        }
    ];

    return <div className="Grid">

        <div className="filter">
            <div>
                <label className="label">Status</label>
                <Dropdown
                    value={filter.active}
                    options={ActiveFilter.statusList}
                    onChange={e => updateFilter('active', e.value)}
                    showClear={true}
                    placeholder='Alla'
                    style={{width: '90px'}}/>
            </div>
            <div>
                <label className="label">Typ</label>
                <Dropdown
                    value={filter.type}
                    options={typeList}
                    onChange={e => updateFilter('type', e.value)}
                    optionValue='id'
                    optionLabel='name'
                    showClear={true}
                    placeholder='Alla'
                    style={{width: '200px'}}/>
            </div>
            <div>
                <label className="label">Finansiering</label>
                <Dropdown
                    value={filter.financingtype}
                    options={financingtypeList}
                    onChange={e => updateFilter('financingtype', e.value)}
                    optionValue='id'
                    optionLabel='nameTranslated'
                    showClear={true}
                    placeholder='Alla'
                    style={{width: '130px'}}/>
            </div>

            <div>
                <label className="label">Intervall</label>
                <div style={{display: 'flex'}}>
                    <OptionalDate
                        value={filter.startDate}
                        onChange={date => updateFilter('startDate', date)}/>
                    <div style={{width: '10px'}}></div>
                    <OptionalDate
                        value={filter.endDate}
                        onChange={date => updateFilter('endDate', date)}/>
                </div>
            </div>

            <div style={{marginLeft: '50px'}}>
                <label className="label">&nbsp;</label>
                <Button label='Lägg till avyttring'
                        icon="pi pi-plus"
                        onClick={() => RouterUtils.navigateTo(props, '/divestments/0')}/>
            </div>


            <div style={{flexGrow: 1, padding: 0}}></div>

            <div>
                <label className="label">Sök</label>
                <InputText className="search" type="search" defaultValue={filter.search}
                           onInput={(e) => updateFilter('search', e.target.value)}/>
            </div>

            <div style={{paddingRight: 0}}>
                <label className="label">&nbsp;</label>
                <ExtraMenu list={filteredList}
                           onExportToExcel={() => NetUtils.exportToExcel('Avyttringar', columns, filteredList)}
                           extraItems={[{
                               label: 'Exportera bokföringsunderlag till Excel',
                               icon: 'pi pi-fw pi-file-excel',
                               command: exportAccountingExcel
                           }]}
                           style={{width: '310px'}}/>
            </div>
        </div>

        <DataTable className="dataTable"
                   value={filteredList}
                   resizableColumns={true}
                   columnResizeMode="expand"
                   paginator={false}
                   footerColumnGroup={footerGroup}
                   selectionMode="single"
                   onRowSelect={e => onRowSelect(e.data)}
                   scrollable={true} scrollHeight="calc(100vh - 240px)"
        >
            {
                columns.map(c => <Column key={c.field}
                                         field={c.field}
                                         header={c.header}
                                         style={c.style}
                                         className={c.className}
                                         body={c.body}
                                         sortable={true}
                />)
            }
        </DataTable>
    </div>
}

export default DivestmentGrid;
